import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, CheckCircle2, CalendarCheck, AlertTriangle, ArrowRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";

const PROPERTY_TYPES = ["apartment", "house", "studio", "condo"];
const TIME_SLOTS = ["morning", "midday", "afternoon", "evening", "night", "flexible"];

const initial = {
  full_name: "", email: "", phone: "",
  nickname: "", address_line1: "", city: "", state: "", zip_code: "", country: "US",
  property_type: "apartment", square_feet: 900, bedrooms: 1, bathrooms: 1, half_bathrooms: 0,
  default_tasks: [], preferred_time_slot: "flexible", cleaning_offset_days: 1,
  additional_notes: "", ical_url: "",
};

function Section({ n, title, children }) {
  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-7 h-7 rounded-lg bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center text-sm font-bold">{n}</span>
        <h3 className="font-display text-lg font-bold">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

export default function HostConnectPage() {
  const { t, locale } = useI18n();
  const [s, setS] = useState(initial);
  const [rule, setRule] = useState(null);
  const [quote, setQuote] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {bookings_created}
  const up = (patch) => setS((p) => ({ ...p, ...patch }));

  useEffect(() => {
    api.get("/pricing/active").then((r) => setRule(unwrap(r))).catch(() => {});
  }, []);

  const tasks = useMemo(() => (rule?.tasks || []).filter((tk) => tk.is_active !== false), [rule]);

  const fetchQuote = useCallback(async () => {
    try {
      const r = await api.post("/hosts/quote", {
        property_type: s.property_type,
        square_feet: Number(s.square_feet) || 200,
        bedrooms: Number(s.bedrooms) || 0,
        bathrooms: Number(s.bathrooms) || 0,
        half_bathrooms: Number(s.half_bathrooms) || 0,
        tasks: s.default_tasks,
        zip_code: s.zip_code,
      });
      setQuote(unwrap(r));
    } catch { /* ignore */ }
  }, [s.property_type, s.square_feet, s.bedrooms, s.bathrooms, s.half_bathrooms, s.default_tasks, s.zip_code]);

  useEffect(() => {
    const id = setTimeout(fetchQuote, 350);
    return () => clearTimeout(id);
  }, [fetchQuote]);

  const toggleTask = (code) =>
    up({ default_tasks: s.default_tasks.includes(code) ? s.default_tasks.filter((c) => c !== code) : [...s.default_tasks, code] });

  const testCalendar = async () => {
    if (!s.ical_url.trim()) { toast.error(t("hosts.test_err")); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/hosts/test-calendar", { ical_url: s.ical_url.trim() });
      setTestResult(unwrap(r));
    } catch {
      setTestResult({ ok: false, events_found: 0, error: "error" });
    } finally {
      setTesting(false);
    }
  };

  const canSubmit = s.full_name && s.email && s.phone && s.nickname && s.address_line1 && s.city && s.zip_code && s.ical_url && Number(s.square_feet) >= 200;

  const submit = async () => {
    if (!canSubmit) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/hosts/listings", {
        ...s,
        square_feet: Number(s.square_feet),
        bedrooms: Number(s.bedrooms),
        bathrooms: Number(s.bathrooms),
        half_bathrooms: Number(s.half_bathrooms),
        cleaning_offset_days: Number(s.cleaning_offset_days),
      });
      const d = unwrap(r);
      setDone({ bookings_created: d?.sync?.bookings_created ?? 0 });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not connect listing");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="host-connect-done">
        <SEO title="Listing connected — Ever Clean Home" noindex />
        <Header />
        <div className="flex-1 grid place-items-center px-4 py-20">
          <Card className="rounded-3xl p-10 border border-[#E5E7EB] max-w-lg text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#0EA5A4]/10 grid place-items-center"><CheckCircle2 className="w-9 h-9 text-[#0EA5A4]" /></div>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-5">{t("hosts.done_title")}</h1>
            <p className="mt-3 text-[#4B5563]">{t("hosts.done_body", { n: done.bookings_created })}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => { setS(initial); setDone(null); setTestResult(null); }} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="host-add-another">
                {t("hosts.add_another")}
              </Button>
              <Link to="/"><Button variant="outline" className="rounded-xl w-full">{t("hosts.back_home")}</Button></Link>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="host-connect-page">
      <SEO title="Connect your listing — Ever Clean Home" description="Connect your Airbnb listing for automatic turnover cleaning." />
      <Header />

      <div className="max-w-3xl mx-auto px-4 md:px-8 w-full py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-bold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> {t("hosts.nav")}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-4">{t("hosts.form_title")}</h1>
        <p className="mt-2 text-[#4B5563]">{t("hosts.form_sub")}</p>

        <div className="mt-7 space-y-5">
          {/* 1 — contact */}
          <Section n={1} title={t("hosts.sec_contact")}>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label={t("hosts.f_full_name")} req><Input value={s.full_name} onChange={(e) => up({ full_name: e.target.value })} className="rounded-xl mt-1" data-testid="host-full-name" /></Field>
              <Field label={t("hosts.f_email")} req><Input type="email" value={s.email} onChange={(e) => up({ email: e.target.value })} className="rounded-xl mt-1" data-testid="host-email" /></Field>
              <Field label={t("hosts.f_phone")} req><Input value={s.phone} onChange={(e) => up({ phone: e.target.value })} className="rounded-xl mt-1" data-testid="host-phone" /></Field>
            </div>
          </Section>

          {/* 2 — listing */}
          <Section n={2} title={t("hosts.sec_listing")}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("hosts.f_nickname")} req><Input value={s.nickname} onChange={(e) => up({ nickname: e.target.value })} placeholder={t("hosts.f_nickname_ph")} className="rounded-xl mt-1" data-testid="host-nickname" /></Field>
              <Field label={t("hosts.f_property")} req>
                <select value={s.property_type} onChange={(e) => up({ property_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm capitalize" data-testid="host-property-type">
                  {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{t(`wizard.properties.${p}`)}</option>)}
                </select>
              </Field>
              <Field label={t("hosts.f_address")} req full><Input value={s.address_line1} onChange={(e) => up({ address_line1: e.target.value })} placeholder="123 Main St" className="rounded-xl mt-1" data-testid="host-address" /></Field>
              <Field label={t("hosts.f_city")} req><Input value={s.city} onChange={(e) => up({ city: e.target.value })} className="rounded-xl mt-1" data-testid="host-city" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("hosts.f_state")}><Input value={s.state} onChange={(e) => up({ state: e.target.value })} placeholder="MA" className="rounded-xl mt-1 font-mono uppercase" data-testid="host-state" /></Field>
                <Field label={t("hosts.f_zip")} req><Input value={s.zip_code} onChange={(e) => up({ zip_code: e.target.value })} placeholder="02108" className="rounded-xl mt-1 font-mono" data-testid="host-zip" /></Field>
              </div>
              <Field label={t("hosts.f_sqft")} req><Input type="number" min={200} value={s.square_feet} onChange={(e) => up({ square_feet: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="host-sqft" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label={t("hosts.f_bedrooms")}><Input type="number" min={0} value={s.bedrooms} onChange={(e) => up({ bedrooms: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="host-bedrooms" /></Field>
                <Field label={t("hosts.f_bathrooms")}><Input type="number" min={0} value={s.bathrooms} onChange={(e) => up({ bathrooms: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="host-bathrooms" /></Field>
                <Field label={t("hosts.f_half")}><Input type="number" min={0} value={s.half_bathrooms} onChange={(e) => up({ half_bathrooms: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="host-half" /></Field>
              </div>
            </div>
          </Section>

          {/* 3 — preferences */}
          <Section n={3} title={t("hosts.sec_prefs")}>
            <Label className="text-sm">{t("hosts.f_tasks")}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {tasks.map((tk) => {
                const selected = s.default_tasks.includes(tk.code);
                const label = locale === "es" ? tk.label_es : tk.label_en;
                return (
                  <button key={tk.code} type="button" onClick={() => toggleTask(tk.code)}
                    className={`text-left rounded-xl p-3 border flex items-center justify-between transition-all ${selected ? "border-[#0EA5A4] bg-[#0EA5A4]/5" : "border-[#E5E7EB] hover:border-[#0EA5A4]/50"}`}
                    data-testid={`host-task-${tk.code}`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded border-2 grid place-items-center ${selected ? "bg-[#0EA5A4] border-[#0EA5A4]" : "border-[#9CA3AF]"}`}>{selected && <CheckCircle2 className="w-3 h-3 text-white" />}</span>
                      <span className="text-sm font-medium">{label}</span>
                    </span>
                    <span className="font-mono text-xs text-[#0EA5A4] font-semibold">+${tk.price.toFixed(2)}</span>
                  </button>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label={t("hosts.f_time")}>
                <select value={s.preferred_time_slot} onChange={(e) => up({ preferred_time_slot: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm" data-testid="host-time">
                  {TIME_SLOTS.map((slot) => <option key={slot} value={slot}>{t(`wizard.step6.time_${slot}`)}</option>)}
                </select>
              </Field>
              <Field label={t("hosts.f_offset")}>
                <div className="flex gap-2 mt-1">
                  {[0, 1, 2].map((o) => (
                    <button key={o} type="button" onClick={() => up({ cleaning_offset_days: o })}
                      className={`flex-1 rounded-xl border px-2 py-2 text-xs font-medium transition-all ${Number(s.cleaning_offset_days) === o ? "border-[#0EA5A4] bg-[#0EA5A4]/5 text-[#0F766E]" : "border-[#E5E7EB]"}`}
                      data-testid={`host-offset-${o}`}>
                      {t(`hosts.offset_${o}`)}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="mt-4">
              <Label className="text-sm">{t("hosts.f_notes")}</Label>
              <Textarea rows={3} value={s.additional_notes} onChange={(e) => up({ additional_notes: e.target.value })} placeholder={t("hosts.f_notes_ph")} className="rounded-xl mt-1" data-testid="host-notes" />
            </div>
          </Section>

          {/* 4 — calendar */}
          <Section n={4} title={t("hosts.sec_calendar")}>
            <div className="flex items-end justify-between gap-2 flex-wrap">
              <Label className="text-sm">{t("hosts.f_ical")} *</Label>
              <Link to="/for-hosts" className="text-xs text-[#0EA5A4] hover:underline font-medium">{t("hosts.f_ical_help")}</Link>
            </div>
            <div className="flex gap-2 mt-1">
              <Input value={s.ical_url} onChange={(e) => { up({ ical_url: e.target.value }); setTestResult(null); }} placeholder="https://www.airbnb.com/calendar/ical/….ics" className="rounded-xl font-mono text-xs" data-testid="host-ical" />
              <Button type="button" onClick={testCalendar} disabled={testing || !s.ical_url} variant="outline" className="rounded-xl shrink-0" data-testid="host-test-calendar">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarCheck className="w-4 h-4 mr-1" /> {t("hosts.test_cal")}</>}
              </Button>
            </div>
            {testResult && (
              testResult.ok ? (
                <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="host-test-ok">
                  <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  {testResult.events_found > 0 ? t("hosts.test_ok", { n: testResult.events_found }) : t("hosts.test_none")}
                  {testResult.next_checkouts?.length > 0 && (
                    <span className="block mt-1 text-xs text-emerald-700 font-mono">{testResult.next_checkouts.join(" · ")}</span>
                  )}
                </div>
              ) : (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="host-test-err">
                  <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {t("hosts.test_err")}
                </div>
              )
            )}
          </Section>

          {/* 5 — pricing */}
          <Section n={5} title={t("hosts.sec_pricing")}>
            {quote?.breakdown ? (
              <div data-testid="host-price-block">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-[#4B5563]">{t("hosts.price_per")}</span>
                  <span className="font-mono text-3xl font-bold text-[#0EA5A4]" data-testid="host-price-total">${quote.breakdown.total.toFixed(2)}</span>
                </div>
                <p className="mt-2 text-xs text-[#4B5563]">{t("hosts.price_note")}</p>
              </div>
            ) : (
              <p className="text-sm text-[#9CA3AF]">{t("hosts.price_hint")}</p>
            )}
          </Section>

          <Button onClick={submit} disabled={!canSubmit || submitting}
            className="w-full h-12 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-base" data-testid="host-submit">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("hosts.submit")} <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>

      <div className="mt-auto"><Footer /></div>
    </div>
  );
}

function Field({ label, req, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-sm">{label}{req && " *"}</Label>
      {children}
    </div>
  );
}
